import React, { useEffect, useRef, useState } from 'react'
import {
	Table, Form, Row, Empty, DatePicker, Col, Typography
} from 'antd'
import { isEmpty } from 'lodash'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
	StringParam, NumericArrayParam, withDefault, useQueryParams
} from 'use-query-params'
import { initialize } from 'redux-form'
import { ColumnsType } from 'antd/es/table'
import moment from 'moment'
import SelectUsersField from '../../components/SelectUsersField'
import { getPayslips, IPayslipTableItem, PayslipStatus } from '../../reducers/payslips/actions'
import { RootState } from '../../reducers'
import PayslipDrawer from '../users/components/PayslipDrawer'

const PAGE_SIZE = 20

const Payslips = () => {
	const dispatch = useDispatch()
	const { t } = useTranslation('pages')

	const payslips = useSelector((state: RootState) => state.payslips.payslips)
	const filterInitialValues = useSelector((state: RootState) => state.form?.PAYSPLIPS_FILTER?.values)

	const [selectedUserID, setSelectedUserID] = useState<number | null>(null)
	const [isPayslipDrawerOpen, setPayslipDrawerOpen] = useState(false)

	const [query, setQuery] = useQueryParams({
		users: withDefault(NumericArrayParam, []),
		month: withDefault(StringParam, moment().format('YYYY-MM'))
	})

	const handleOpenDrawer = (userID: number) => {
		setSelectedUserID(userID)
		setPayslipDrawerOpen(true)
	}

	const handleCloseDrawer = () => {
		setPayslipDrawerOpen(false)
		setSelectedUserID(null)
	}

	const userSelectRef = useRef<any>(null)

	useEffect(() => {
		dispatch(getPayslips({
			page: 1,
			limit: PAGE_SIZE,
			queryParams: {
				users: query.users,
				month: query.month
			}
		}))
	}, [dispatch, query.users, query.month])

	useEffect(() => {
		if (!filterInitialValues || isEmpty(filterInitialValues)) {
			dispatch(initialize('PAYSPLIPS_FILTER', {
				month: query.month
			}))
		}
	}, [dispatch, filterInitialValues, query.month])

	const columns: ColumnsType<IPayslipTableItem> = [
		{
			title: t('pages:Zamestnanec'),
			dataIndex: ['user', 'fullName'],
			key: 'user.fullName'
		},
		{
			title: t('pages:Mesiac'),
			dataIndex: 'month',
			key: 'month'
		},
		{
			title: t('pages:Základný plat'),
			dataIndex: 'paycheckFromManager',
			key: 'paycheckFromManager'
		},
		{
			title: t('pages:Bonus'),
			dataIndex: 'annualCleaningBonus',
			key: 'annualCleaningBonus'
		},
		{
			title: t('pages:Výsledná výplata'),
			dataIndex: 'finalSalary',
			key: 'finalSalary'
		},
		{
			title: t('pages:Status'),
			dataIndex: 'status',
			key: 'status',
			// eslint-disable-next-line react/display-name
			render: (status) => {
				if (status === 'FINALIZED') return <span style={{ color: 'green' }}>{t('pages:Ukončená')}</span>
				if (status === 'SAVED') return <span style={{ color: 'orange' }}>{t('pages:Uložená')}</span>
				return <span style={{ color: 'red' }}>{t('pages:Návrh')}</span>
			}
		}
	]

	const selectedMonth = moment(query.month, 'YYYY-MM').locale('sk').format('MMMM YYYY')

	return (
		<div className={'page-wrapper'}>
			<Row justify="space-between" align="middle">
				<Col>
					<Row gutter={16}>
						<Form.Item className={'ml-2'}>
							<SelectUsersField
								ref={userSelectRef}
								onValueChange={(data) => {
									setQuery({ ...query, users: data.value.map((item) => item.value) })
								}}
								userValues={query.users}
								multiple={false}
								placeholder={t('pages:Vyber zamestnanca')}
							/>
						</Form.Item>
						<Form.Item label={t('pages:Mesiac')} className={'font-semibold ml-2'}>
							<DatePicker
								picker="month"
								value={moment(query.month, 'YYYY-MM') as any}
								onChange={(date) => {
									if (date) {
										setQuery({ ...query, month: date.format('YYYY-MM') })
									}
								}}
								allowClear={false}
							/>
						</Form.Item>
					</Row>
				</Col>
				<Col>
					<Typography.Title level={4} className="text-right uppercase text-gray-500 mb-0">
						{selectedMonth}
					</Typography.Title>
				</Col>
			</Row>

			<Table
				className={'general-table'}
				columns={columns}
				dataSource={payslips.tableData}
				pagination={{
					pageSize: PAGE_SIZE,
					total: payslips.pagination?.totalCount,
					current: payslips.pagination?.page,
					showSizeChanger: false
				}}
				rowClassName={(record) => (record.status === PayslipStatus.DRAFT ? 'row-draft' : '')}
				loading={payslips.isLoading}
				onRow={(record) => ({
					onClick: () => handleOpenDrawer(record.user.id)
				})}
				locale={{
					emptyText: <Empty description={t('pages:Žiadne dáta')} />
				}}
				size={'small'}
				summary={(pageData) => {
					const totalBase = Number(pageData?.reduce((sum, row) => sum + (row?.paycheckFromManager || 0), 0) || 0)
					const totalBonus = Number(pageData?.reduce((sum, row) => sum + (row?.annualCleaningBonus || 0), 0) || 0)
					const totalFinal = Number(pageData?.reduce((sum, row) => sum + (row?.finalSalary || 0), 0) || 0)

					return (
						<Table.Summary.Row>
							<Table.Summary.Cell index={0}><strong>{t('pages:Súčet')}</strong></Table.Summary.Cell>
							<Table.Summary.Cell index={1} />
							<Table.Summary.Cell index={2}><strong>{totalBase.toFixed(2)} €</strong></Table.Summary.Cell>
							<Table.Summary.Cell index={3}><strong>{totalBonus.toFixed(2)} €</strong></Table.Summary.Cell>
							<Table.Summary.Cell index={4}><strong>{totalFinal.toFixed(2)} €</strong></Table.Summary.Cell>
							<Table.Summary.Cell index={5} />
						</Table.Summary.Row>

					)
				}}
			/>

			{
				selectedUserID && (
					<PayslipDrawer
						userID={selectedUserID}
						onClose={handleCloseDrawer}
						open={isPayslipDrawerOpen}
						initialMonth={moment(query.month, 'YYYY-MM')}
						onRefreshList={() => {
							dispatch(getPayslips({
								page: payslips.pagination?.page || 1,
								limit: PAGE_SIZE,
								queryParams: {
									users: query.users,
									month: query.month
								}
							}))
						}
						}
					/>
				)
			}
		</div>
	)
}

export default Payslips
